import React, { useEffect, useState, useCallback } from 'react';
import { useSelector } from 'react-redux';

const STORAGE_KEY = 'flea_location_permission_education_shown';

/**
 * One-time education after first sign-in on the website.
 * Browsers don't mirror Android's three options exactly; copy stays honest.
 */
export default function LocationPermissionEducationModal() {
  const { isAuthenticated, user } = useSelector((state) => state.auth);
  const [open, setOpen] = useState(false);

  const markShown = useCallback(() => {
    try {
      localStorage.setItem(STORAGE_KEY, 'true');
    } catch {
      /* ignore */
    }
  }, []);

  useEffect(() => {
    if (!isAuthenticated || !user) return undefined;

    let cancelled = false;
    let timerId;

    (async () => {
      try {
        const shown = localStorage.getItem(STORAGE_KEY);
        if (shown || cancelled) return;

        if (navigator.permissions?.query) {
          try {
            const status = await navigator.permissions.query({ name: 'geolocation' });
            if (status.state === 'granted') {
              markShown();
              return;
            }
          } catch {
            /* Permissions API not fully supported */
          }
        }

        timerId = window.setTimeout(() => {
          if (!cancelled) setOpen(true);
        }, 1600);
      } catch {
        /* ignore */
      }
    })();

    return () => {
      cancelled = true;
      if (timerId) window.clearTimeout(timerId);
    };
  }, [isAuthenticated, user, markShown]);

  const close = () => {
    markShown();
    setOpen(false);
  };

  const handleContinue = () => {
    markShown();
    setOpen(false);
    if (navigator.geolocation && typeof navigator.geolocation.getCurrentPosition === 'function') {
      navigator.geolocation.getCurrentPosition(
        () => {},
        () => {},
        { timeout: 15000, maximumAge: 0 }
      );
    }
  };

  const handleNotNow = () => {
    close();
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[12000] flex items-center justify-center bg-black/50 px-4 py-8"
      role="dialog"
      aria-modal="true"
      aria-labelledby="flea-loc-edu-title"
    >
      <div className="bg-white rounded-2xl shadow-xl max-w-md w-full max-h-[90vh] overflow-hidden flex flex-col">
        <div className="p-6 overflow-y-auto">
          <div className="text-5xl text-center mb-3" aria-hidden>
            📍
          </div>
          <h2 id="flea-loc-edu-title" className="text-xl font-bold text-gray-900 text-center mb-2">
            Location on this website
          </h2>
          <p className="text-gray-600 text-center text-sm mb-4 leading-relaxed">
            If your browser asks to use your location, here’s what the choices usually mean:
          </p>

          <div className="space-y-4 text-sm text-gray-700 border-t border-gray-100 pt-4">
            <div>
              <p className="font-semibold text-gray-900 mb-1">Allow once / only while you’re on the site</p>
              <p className="leading-relaxed text-gray-600">
                Location may be used for this visit or until you close the tab. You might be asked again next
                time you sign in or open checkout or maps features.
              </p>
            </div>
            <div>
              <p className="font-semibold text-gray-900 mb-1">Allow while using the site (same tab)</p>
              <p className="leading-relaxed text-gray-600">
                Works while Flea is open in your browser—for example helping with addresses or nearby items.
              </p>
            </div>
            <div>
              <p className="font-semibold text-gray-900 mb-1">Always allow (if your browser offers it)</p>
              <p className="leading-relaxed text-gray-600">
                The site can prompt for location on future visits without asking every time. Websites generally
                do not run in the background like an installed app; delivery features still use location while you’re
                using Flea in the browser.
              </p>
            </div>
          </div>

          <p className="text-xs text-gray-400 italic mt-4 leading-relaxed">
            Wording depends on your browser (Chrome, Safari, Firefox, etc.).
          </p>
        </div>

        <div className="flex gap-3 p-4 pt-0 border-t border-gray-100 bg-gray-50">
          <button
            type="button"
            onClick={handleNotNow}
            className="flex-1 py-3 px-4 rounded-lg border border-gray-300 bg-white text-gray-800 font-semibold hover:bg-gray-100"
          >
            Not now
          </button>
          <button
            type="button"
            onClick={handleContinue}
            className="flex-1 py-3 px-4 rounded-lg bg-blue-600 text-white font-semibold hover:bg-blue-700"
          >
            Continue
          </button>
        </div>
      </div>
    </div>
  );
}
